import { ClickAwayListener, makeStyles } from '@material-ui/core';
import { mergeClasses } from '@material-ui/styles';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import Button from '~components/button';
import { BasicClose } from '~svg-components';
import useForkRef from '~utils/use-fork-ref';
import Tooltip from './tooltip';

const useTooltipStyles = makeStyles((theme) => ({
  popper: {
    opacity: 1,
  },
  popperInteractive: {},
  popperArrow: {},
  tooltip: {
    position: ({ withCloseButton }) => withCloseButton && 'relative',
    padding: 0,
    maxWidth: 'unset',
    backgroundColor: theme.palette.common.white,
  },
  tooltipArrow: {},
  arrow: {
    color: 'unset',
  },
  touch: {},
  tooltipPlacementLeft: {},
  tooltipPlacementRight: {},
  tooltipPlacementTop: {},
  tooltipPlacementBottom: {},
}));

const useStyles = makeStyles({
  closeButton: {
    position: 'absolute',
    width: 32,
    height: 32,
    top: 8,
    right: 8,
  },
});

const TooltipClick = React.forwardRef((props, ref) => {
  const {
    title,
    withCloseButton,
    children,
    classes: classesProp,
    open: openProp,
    onOpen,
    onClose,
    disabled,
    ...otherProps
  } = props;

  const rootRef = useRef();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = useRef(openProp !== undefined && openProp !== null);
  const open = isControlled.current ? openProp : internalOpen;

  const handleOpen = (e) => {
    if (!isControlled.current) setInternalOpen(true);
    if (onOpen) onOpen(e);
  };

  const handleClickAway = (e) => {
    if (!rootRef.current.contains(e.target)) {
      if (!isControlled.current) setInternalOpen(false);
      if (onClose) onClose(e);
    }
  };

  const handleClick = (e) => {
    handleOpen(e);
    if (children.props.onClick) children.props.onClick(e);
  };

  const classes = useStyles();

  return (
    <Tooltip
      disableFocusListener
      disableHoverListener
      disableTouchListener
      interactive
      placement="bottom-start"
      disabled={disabled}
      {...otherProps}
      title={
        <>
          {withCloseButton && (
            <Button variant="text" className={classes.closeButton}>
              <BasicClose fontSize={13} color="grey66" />
            </Button>
          )}
          <ClickAwayListener touchEvent={false} onClickAway={handleClickAway}>
            {title}
          </ClickAwayListener>
        </>
      }
      open={open}
      classes={mergeClasses({
        baseClasses: useTooltipStyles({ withCloseButton }),
        newClasses: classesProp,
      })}
      ref={useForkRef(ref, rootRef)}
    >
      {React.cloneElement(children, { onClick: disabled ? undefined : handleClick })}
    </Tooltip>
  );
});

TooltipClick.propTypes = {
  arrow: PropTypes.bool,
  withCloseButton: PropTypes.bool,
  children: PropTypes.node.isRequired,
  classes: PropTypes.object,
  disableFocusListener: PropTypes.bool,
  disableHoverListener: PropTypes.bool,
  disableTouchListener: PropTypes.bool,
  enterDelay: PropTypes.number,
  enterTouchDelay: PropTypes.number,
  id: PropTypes.string,
  leaveDelay: PropTypes.number,
  leaveTouchDelay: PropTypes.number,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  disabled: PropTypes.bool,
  title: PropTypes.node.isRequired,
  TransitionComponent: PropTypes.elementType,
};

TooltipClick.defaultProps = {
  arrow: undefined,
  withCloseButton: undefined,
  classes: {},
  disableFocusListener: undefined,
  disableHoverListener: undefined,
  disableTouchListener: undefined,
  enterDelay: undefined,
  enterTouchDelay: undefined,
  id: undefined,
  leaveDelay: undefined,
  leaveTouchDelay: undefined,
  onClose: undefined,
  onOpen: undefined,
  open: undefined,
  disabled: undefined,
  TransitionComponent: undefined,
};

export default TooltipClick;
